import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LoadingIndicatorDirective } from '@zero/shared/ui-directives';
import { UserMessagesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, InputComponent } from '@zero/shared/ui-web-design-system';
import { UserMessage } from '@zero/shared/util-models';
import { RouteOptionsService, UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { BehaviorSubject, map } from 'rxjs';
import { EmailPipe } from '../../pipes/validation/email/email.pipe';
import { SignUpService } from '../../services/sign-up.service';

@Component({
  templateUrl: './sign-up-resend.component.html',
  imports: [
    LoadingIndicatorDirective,
    FormsModule,
    UserMessagesComponent,
    RouterLink,
    TranslocoPipe,
    ButtonComponent,
    InputComponent,
    AsyncPipe,
    EmailPipe,
  ],
  styleUrls: ['./sign-up-resend.component.scss'],
})
export class SignUpResendComponent implements OnInit, OnDestroy {
  public ongoingRequest = false;

  public readonly signupService = inject(SignUpService);
  readonly #translateService = inject(TranslocoService);
  readonly #messageService = inject(UserMessagesHolderService);
  readonly #router = inject(Router);
  readonly #routeOptionsService = inject(RouteOptionsService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);

  readonly #email = new BehaviorSubject<string>('');
  public email$ = this.#email.asObservable();

  public isFormValid$ = this.email$.pipe(map((email) => !!email && new EmailPipe().transform(email)));

  public ngOnInit(): void {
    this.#routeOptionsService.hideHeaderAndFooter();
    this.#routeOptionsService.toggleGradient();
    this.#messageService.clearAllMessages();
  }

  public ngOnDestroy(): void {
    this.#routeOptionsService.displayHeaderAndFooter();
    this.#routeOptionsService.toggleGradient();
  }

  public handleEmailChange = (email: string) => this.#email.next(email.toLowerCase());

  public onSubmit(): void {
    this.#messageService.clearAllMessages();
    this.ongoingRequest = true;
    this.signupService
      .resendConfirmationMail(this.#email.value.toLowerCase())
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        this.ongoingRequest = false;
        if (result.success) {
          this.#router.navigate([commonRoutes.prelogin.onboarding.emailConfirmation], {
            relativeTo: this.#activatedRoute,
          });
        } else {
          this.#messageService.addMessage(
            new UserMessage(result.message ?? this.#translateService.translate('register.sign-up-resend.fail-message'), 'danger', true),
          );
        }
      });
  }
}
